/**
 * ConditionPopover Component
 *
 * A reusable popover wrapper for displaying condition details.
 * Used by ConditionWithFetch and ConditionsPreviewButton.
 */

import type { ReactNode } from 'react';
import {
  Popover,
  PopoverTriggerButton,
  PopoverContent,
  Panel,
  PanelHeader,
  PanelTitle,
  PanelSection,
  Stack,
  Text,
} from '@customerio/pluma-components/react';
import type { IconName } from '@customerio/pluma-icons';
import * as styles from './index.css';
import { POPOVER_MAX_HEIGHT } from './constants';

interface ConditionPopoverProps {
  /** Title for the popover header */
  title: string;
  /** Optional subtitle shown below the title */
  subtitle?: string;
  /** Content to display in the popover body */
  children: ReactNode;
  /** Optional footer content (e.g., links) */
  footer?: ReactNode;
  /** Popover placement */
  placement?: 'top' | 'bottom' | 'left' | 'right';
  /** Callback when popover opens/closes */
  onOpenChange?: (isOpen: boolean) => void;
  /** Aria label for the trigger button (only used for icon-only trigger) */
  ariaLabel?: string;
  /** Icon for the trigger button */
  icon?: IconName;
  /** Optional button label - if provided, shows a labeled button instead of icon-only */
  buttonLabel?: string;
}

/**
 * A reusable popover component for displaying condition details.
 * Provides consistent styling and behavior across all condition popovers.
 */
export function ConditionPopover({
  title,
  subtitle,
  children,
  footer,
  placement = 'bottom',
  onOpenChange,
  ariaLabel,
  icon = 'segment',
  buttonLabel,
}: ConditionPopoverProps) {
  return (
    <Popover
      shouldTriggerOnClick={true}
      shouldTriggerOnHover={false}
      placement={placement}
      initialFocus={-1}
      onOpenChange={onOpenChange}
    >
      {buttonLabel != null ? (
        <PopoverTriggerButton variant="secondary" size="small" icon={icon} iconPosition="leading">
          {buttonLabel}
        </PopoverTriggerButton>
      ) : (
        <PopoverTriggerButton
          variant="tertiary"
          size="small"
          icon={icon}
          aria-label={ariaLabel ?? `View ${title}`}
          py="0"
          px="050"
          style={{ minHeight: 'auto' }}
        />
      )}
      <PopoverContent p="0">
        <Panel withSectionBorders className={styles.popoverContent}>
          <PanelHeader py="150" px="300">
            <Stack gap="050">
              <PanelTitle level="5" size="m">
                {title}
              </PanelTitle>
              {subtitle != null ? (
                <Text size="s" color="subtle">
                  {subtitle}
                </Text>
              ) : null}
            </Stack>
          </PanelHeader>
          <PanelSection py="150" px="300" overflowY="auto" style={{ maxHeight: POPOVER_MAX_HEIGHT }}>
            {children}
          </PanelSection>
          {footer != null ? (
            <PanelSection py="100" px="300">
              {footer}
            </PanelSection>
          ) : null}
        </Panel>
      </PopoverContent>
    </Popover>
  );
}
